<script setup lang="ts">
/**
 * **值查看器**：把"这一行缩略"展开成"完整值"。
 *
 * ## 三种形态（按值的形状选，而不是一刀切 JSON）
 *
 * | 值 | 怎么看 |
 * |---|---|
 * | 列表 | **表格**（序号 + 值）—— 抓回来的多行结果大多是这个形状 |
 * | 文本 | 原样多行（可选中复制）—— 正文/HTML 要看清楚 |
 * | 其它 | 格式化 JSON（对象、数字、布尔） |
 *
 * ⚠️ 为什么不做"自动判断该用什么控件"那种花活：值的类型是**运行时**才知道的，
 * 猜错的时候用户看到的是一个不合适的视图（比如把 JSON 当纯文本铺开）。
 * 三种明确形态、按 `Array.isArray` 和 `typeof` 分派，够了。
 */
import { computed } from 'vue'
import { valueKind } from '@/node/evaluate'

const props = defineProps<{ value: unknown }>()

const kind = computed(() => valueKind(props.value))
const isList = computed(() => Array.isArray(props.value))
const isText = computed(() => typeof props.value === 'string')
const list = computed<unknown[]>(() => (Array.isArray(props.value) ? props.value : []))
const pretty = computed(() => {
  try {
    return JSON.stringify(props.value, null, 2) ?? String(props.value)
  } catch {
    return String(props.value)
  }
})
</script>

<template>
  <div class="np-value" data-np-value-full>
    <div class="np-value__head">
      <span class="np-value__kind">{{ kind }}</span>
      <span v-if="isList" class="np-value__count">{{ list.length }} 项</span>
    </div>

    <table v-if="isList" class="np-value__table">
      <tbody>
        <tr v-for="(item, index) in list" :key="index">
          <td class="np-value__index">{{ index }}</td>
          <td class="np-value__cell">{{ typeof item === 'string' ? item : JSON.stringify(item) }}</td>
        </tr>
      </tbody>
    </table>

    <pre v-else-if="isText" class="np-value__text">{{ value }}</pre>
    <pre v-else class="np-value__json">{{ pretty }}</pre>
  </div>
</template>
